<template>
  <div class="acknowledgment-pipeline-page">
    <b-card class="mb-2">
      <b-row class="align-items-center">
        <b-col
          cols="12"
          md="6"
        >
          <h4 class="mb-0">
            <feather-icon
              icon="CheckCircleIcon"
              size="18"
              class="mr-50"
            />
            لوحة اقرارات الرفع المساحي
          </h4>
        </b-col>
        <b-col
          cols="12"
          md="6"
          class="text-md-right mt-1 mt-md-0"
        >
          <b-form-checkbox
            v-model="filters.mine"
            class="d-inline-flex align-items-center mr-1 mb-50 mb-md-0"
            @change="onFilterChange"
          >
            مهامي
          </b-form-checkbox>
          <b-form-checkbox
            v-model="filters.has_chat"
            class="d-inline-flex align-items-center mr-1 mb-50 mb-md-0"
            @change="onFilterChange"
          >
            لديها محادثة
          </b-form-checkbox>
          <b-button
            variant="outline-primary"
            :disabled="loading"
            @click="loadBoard"
          >
            <feather-icon
              icon="RefreshCwIcon"
              size="16"
              :class="{ 'rotate-animation': loading }"
            />
          </b-button>
        </b-col>
      </b-row>

      <b-row class="mt-1">
        <b-col
          cols="12"
          md="6"
          lg="4"
        >
          <b-form-input
            v-model="filters.keyword"
            placeholder="بحث بالاسم أو كود الرفع أو رقم المشروع"
          />
        </b-col>
        <b-col
          cols="12"
          md="3"
          lg="2"
          class="mt-1 mt-md-0"
        >
          <b-form-select
            v-model="filters.step_status"
            :options="stepStatusOptions"
            @change="onFilterChange"
          />
        </b-col>
      </b-row>

      <b-row class="mt-2">
        <b-col
          v-for="stat in statTiles"
          :key="stat.key"
          cols="6"
          md="2"
          class="mb-1 mb-md-0"
        >
          <div
            class="stat-tile"
            :class="{ active: isStatActive(stat) }"
            @click="applyStatFilter(stat)"
          >
            <b-avatar
              :variant="stat.variant"
              size="28"
              rounded
            >
              <feather-icon
                :icon="stat.icon"
                size="14"
              />
            </b-avatar>
            <div class="ml-50">
              <div class="stat-count mb-0">
                {{ stats[stat.key] || 0 }}
              </div>
              <small class="text-muted">{{ stat.label }}</small>
            </div>
          </div>
        </b-col>
      </b-row>
    </b-card>

    <div
      v-if="loading && !projects.length"
      class="text-center py-5"
    >
      <b-spinner
        variant="primary"
        label="جاري التحميل..."
      />
    </div>

    <div v-else>
      <acknowledgment-pipeline-kanban-view
        :projects="projects"
        @open-chat="openChat"
        @refresh="refreshBoard"
      />

      <div
        v-if="meta.last_page > 1"
        class="d-flex justify-content-center mt-1"
      >
        <b-pagination
          v-model="filters.page"
          :total-rows="meta.total"
          :per-page="meta.per_page"
          align="center"
          @change="onPageChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BSpinner,
  BAvatar,
  BFormCheckbox,
  BFormInput,
  BFormSelect,
  BPagination,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { getAcknowledgmentPipeline } from '@/api/projects/acknowledgmentPipeline'
import AcknowledgmentPipelineKanbanView from './AcknowledgmentPipelineKanbanView.vue'

const emptyStats = () => ({
  surveyor: 0,
  giser: 0,
  geodesia: 0,
  completed: 0,
  draft: 0,
  mine: 0,
})

export default {
  name: 'AcknowledgmentPipelinePage',
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BSpinner,
    BAvatar,
    BFormCheckbox,
    BFormInput,
    BFormSelect,
    BPagination,
    AcknowledgmentPipelineKanbanView,
  },
  data() {
    return {
      loading: false,
      keywordTimer: null,
      projects: [],
      stats: emptyStats(),
      meta: {
        current_page: 1,
        per_page: 50,
        total: 0,
        last_page: 1,
      },
      filters: {
        keyword: '',
        mine: true,
        has_chat: false,
        step_status: '',
        current_step: '',
        page: 1,
        per_page: 50,
      },
      stepStatusOptions: [
        { value: '', text: 'كل الحالات' },
        { value: 'not_started', text: 'لم يبدأ' },
        { value: 'draft', text: 'مسودة' },
      ],
      statTiles: [
        {
          key: 'surveyor',
          label: 'اقرار المساح',
          icon: 'UserIcon',
          variant: 'light-warning',
          type: 'step',
        },
        {
          key: 'giser',
          label: 'منطقية الرفع',
          icon: 'MapIcon',
          variant: 'light-info',
          type: 'step',
        },
        {
          key: 'geodesia',
          label: 'الجيوديسيا',
          icon: 'CompassIcon',
          variant: 'light-primary',
          type: 'step',
        },
        {
          key: 'completed',
          label: 'مكتملة',
          icon: 'CheckIcon',
          variant: 'light-success',
          type: 'step',
        },
        {
          key: 'draft',
          label: 'مسودات',
          icon: 'EditIcon',
          variant: 'light-secondary',
          type: 'status',
        },
        {
          key: 'mine',
          label: 'مهامي',
          icon: 'StarIcon',
          variant: 'light-danger',
          type: 'mine',
        },
      ],
    }
  },
  watch: {
    'filters.keyword'() {
      if (this.keywordTimer) clearTimeout(this.keywordTimer)
      this.keywordTimer = setTimeout(() => {
        this.onFilterChange()
      }, 400)
    },
  },
  mounted() {
    this.loadBoard()
  },
  beforeDestroy() {
    if (this.keywordTimer) clearTimeout(this.keywordTimer)
  },
  methods: {
    unwrapResponse(response) {
      const root = response && response.success !== undefined
        ? response
        : ((response && response.data) || response || {})
      const payload = root.data || root
      const projects = Array.isArray(payload.projects)
        ? payload.projects
        : (Array.isArray(payload) ? payload : [])
      const stats = { ...emptyStats(), ...(payload.stats || {}) }
      const meta = root.meta || payload.meta || this.meta
      return { projects, stats, meta }
    },
    buildParams() {
      const params = {
        page: this.filters.page,
        per_page: this.filters.per_page,
        include: 'actors,chat,history,center',
        mine: this.filters.mine ? 1 : 0,
      }
      if (this.filters.keyword) params.keyword = this.filters.keyword
      if (this.filters.step_status) params.step_status = this.filters.step_status
      if (this.filters.current_step) params.current_step = this.filters.current_step
      if (this.filters.has_chat) params.has_chat = 1
      return params
    },
    async loadBoard({ silent } = {}) {
      if (!silent) this.loading = true
      try {
        const response = await getAcknowledgmentPipeline(this.buildParams())
        const { projects, stats, meta } = this.unwrapResponse(response)
        this.projects = projects
        this.stats = stats
        this.meta = {
          current_page: meta.current_page || 1,
          per_page: meta.per_page || this.filters.per_page,
          total: meta.total || projects.length,
          last_page: meta.last_page || 1,
        }
      } catch (error) {
        this.projects = []
        this.stats = emptyStats()
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'خطأ',
            icon: 'XIcon',
            variant: 'danger',
            text: this.apiErrorMessage(error),
          },
        })
      } finally {
        this.loading = false
      }
    },
    refreshBoard() {
      return this.loadBoard({ silent: true })
    },
    onFilterChange() {
      this.filters.page = 1
      this.loadBoard()
    },
    onPageChange(page) {
      this.filters.page = page
      this.loadBoard()
    },
    isStatActive(stat) {
      if (stat.type === 'step') {
        return this.filters.current_step === stat.key
      }
      if (stat.type === 'status') {
        return this.filters.step_status === 'draft'
      }
      return this.filters.mine === true
    },
    applyStatFilter(stat) {
      if (stat.type === 'step') {
        this.filters.current_step = this.filters.current_step === stat.key ? '' : stat.key
      } else if (stat.type === 'status') {
        this.filters.step_status = this.filters.step_status === 'draft' ? '' : 'draft'
      } else if (stat.type === 'mine') {
        this.filters.mine = !this.filters.mine
      }
      this.onFilterChange()
    },
    openChat(project) {
      const chatId = (project.chat && project.chat.id) || project.chat_id
      if (!chatId) return
      this.$store.dispatch('surveyChatsUi/open', {
        chatId,
        projectId: project.id,
      })
    },
    apiErrorMessage(error) {
      return (error && error.response && error.response.data && error.response.data.message)
        || 'تعذر تحميل لوحة الاقرارات'
    },
  },
}
</script>

<style lang="scss" scoped>
.acknowledgment-pipeline-page {
  .rotate-animation {
    animation: rotate 1s linear infinite;
  }

  @keyframes rotate {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
}

.stat-tile {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 0.2rem;
  border-radius: 0.4rem;
  border: 1px solid transparent;

  &:hover,
  &.active {
    background: #f8f8f8;
    border-color: #e9ecef;
  }
}

.stat-count {
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.1;
}
</style>
